import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import {
  getFinancialAidUploadFileValidationError,
  inferFinancialAidUploadFileType,
} from "../../../../financial-aid/financialAidUploadFile";
import { CheckCircle2, Download, ExternalLink, FileText, Plus, RefreshCw } from "lucide-react";
import type { FinancialAidStaffDetailContext } from "../ApplicantDetail";
import {
  useConfirmFinancialAidDocumentUpload,
  useGenerateFinancialAidApplicationReport,
  useGetFinancialAidApplicationReportDownloadUrl,
  useListFinancialAidApplicationReportArtifacts,
  useUploadFinancialAidDocument,
} from "../../../../financial-aid/hooks/useFinancialAidApi";
import { buildBrowserUploadHeaders } from "../../../../financial-aid/utils/uploadHeaders";
import { SupportErrorMessage } from "../../../../components/overlays/EngineeringSupportBanner";
import { asArray, asRecord } from "../../utils/domainData";
import {
  PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY,
  readParentFinancialAidRelationshipKey,
  readParentSnapshotTextFallback,
  readRawBoolean,
} from "./staffFinancialAidRawSnapshot";
import {
  FINANCIAL_AID_REPORT_STALE_CHANNEL,
  financialAidReportStaleStorageKey,
  readFinancialAidReportStaleSignal,
} from "../../reportStaleSignals";

const isDefined = <T,>(value: T | null | undefined): value is T => value != null;
type FinancialAidUploadInfo = {
  uploadUrl?: string;
  s3Key?: string;
  documentId?: string;
  uploadHeaders?: Record<string, string>;
};

type FinancialAidDocumentRecord = Record<string, unknown>;
type ReportVariant = "summary" | "full" | "yst";
type ReportArtifactRecord = Record<string, unknown> & {
  artifactId?: string;
  applicationId?: string;
  variant?: ReportVariant;
  fileName?: string;
  appUrl?: string;
  downloadUrl?: string;
  generatedAt?: number;
  expiresAt?: number;
  appendix?: {
    includedCount?: number;
    omittedCount?: number;
    failedCount?: number;
    sources?: Array<Record<string, unknown>>;
  };
};
type ReportPopupWindow = Window | null;
type ReportErrorPresentation = {
  message: string;
  technicalDetails?: string;
};
type ReportPreviewUrlState = {
  artifactId: string;
  url: string;
  expiresAt: number;
};
type ReportPopupStaleGuard = {
  applicationId?: string;
  artifactId?: string;
  generatedAt?: unknown;
};
type ReportOpenPreviewState = {
  artifactId?: string;
  expiresAt?: unknown;
  fileName?: string;
  generatedAt?: unknown;
  openInNewTab?: boolean;
};

const readText = (value: unknown, defaultValue = ""): string => {
  const next = String(value ?? "").trim();
  return next || defaultValue;
};

const REPORT_PDF_LINK_ERROR =
  "We couldn't open the PDF link. Please regenerate the report and try again.";
const REPORT_APP_CONTEXT_ERROR =
  "We couldn't identify this application. Go back to the review list and open it again.";
const REPORT_GENERATED_WITHOUT_LINK_ERROR =
  "The report was generated, but the PDF link was missing. Try Generate again.";
const REPORT_PREVIEW_LOAD_ERROR =
  "We couldn't load the embedded PDF preview. Use Download or regenerate the report.";
const REPORT_PDF_EXPIRY_ERROR =
  "The PDF link was missing its security expiration. Please regenerate the report and try again.";
const REPORT_ARTIFACT_NOT_FOUND_ERROR =
  "We couldn't find this generated report. Regenerate the report and try again.";
const REPORT_STALE_AFTER_CORRECTION_ERROR =
  "This report is stale after a staff correction. Regenerate the report before opening or downloading the PDF.";
const REPORT_FILE_UNAVAILABLE_ERROR =
  "This generated report file is unavailable. Regenerate the report before opening or downloading the PDF.";
const REPORT_PREVIEW_EXPIRY_SAFETY_SECONDS = 60;

const toErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return readText(error.message);
  }
  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    const direct = readText(record.message);
    if (direct) return direct;
    const nestedError = record.error as Record<string, unknown> | undefined;
    const nestedData = record.data as Record<string, unknown> | undefined;
    const nested =
      readText(nestedError?.message) ||
      readText(nestedData?.message) ||
      readText(
        (nestedData?.error as Record<string, unknown> | undefined)?.message,
      );
    if (nested) return nested;
    try {
      const serialized = JSON.stringify(error);
      if (serialized && serialized !== "{}") {
        return serialized;
      }
    } catch {
      // ignore serialization errors
    }
  }
  return readText(String(error ?? ""));
};

const toReportError = (error: unknown, fallback: string): ReportErrorPresentation => {
  const message = toErrorMessage(error);
  if (!message) return { message: fallback };
  if (
    message === REPORT_STALE_AFTER_CORRECTION_ERROR ||
    /stale after a staff correction/i.test(message)
  ) {
    return {
      message: REPORT_STALE_AFTER_CORRECTION_ERROR,
    };
  }
  if (
    /NoSuchKey/i.test(message) ||
    /specified key does not exist/i.test(message)
  ) {
    return {
      message: REPORT_FILE_UNAVAILABLE_ERROR,
      technicalDetails: message,
    };
  }
  if (
    message === REPORT_PDF_LINK_ERROR ||
    /report pdf url was not returned as an absolute http\(s\) link/i.test(message) ||
    /report pdf link was returned too close to expiration/i.test(message)
  ) {
    return {
      message: REPORT_PDF_LINK_ERROR,
      technicalDetails: message === REPORT_PDF_LINK_ERROR ? undefined : message,
    };
  }
  if (
    message === REPORT_PDF_EXPIRY_ERROR ||
    /report pdf link was returned without a usable expiration/i.test(message)
  ) {
    return {
      message: REPORT_PDF_EXPIRY_ERROR,
      technicalDetails: message === REPORT_PDF_EXPIRY_ERROR ? undefined : message,
    };
  }
  if (
    message === REPORT_GENERATED_WITHOUT_LINK_ERROR ||
    /report generation did not return a pdf url/i.test(message)
  ) {
    return {
      message: REPORT_GENERATED_WITHOUT_LINK_ERROR,
      technicalDetails: message === REPORT_GENERATED_WITHOUT_LINK_ERROR ? undefined : message,
    };
  }
  if (/application id/i.test(message)) {
    return {
      message: REPORT_APP_CONTEXT_ERROR,
      technicalDetails: message === REPORT_APP_CONTEXT_ERROR ? undefined : message,
    };
  }
  return {
    message: fallback,
    technicalDetails: message === fallback ? undefined : message,
  };
};

const asNumber = (value: unknown): number => {
  const numeric = typeof value === "number" ? value : Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const asOptionalNonNegativeInteger = (value: unknown): number | null => {
  const normalized =
    typeof value === "string"
      ? value.replace(/[$,\s]/g, "")
      : value;
  const numeric = typeof normalized === "number" ? normalized : Number(normalized);
  return Number.isFinite(numeric) && numeric >= 0 ? Math.floor(numeric) : null;
};

const resolvePrintableReportChildCounts = (
  familyInfo: Record<string, unknown>,
  reviewSummary: Record<string, unknown>,
  rawSnapshot: Record<string, unknown>,
): { totalChildren: number; eligibleChildren: number } => {
  const contract = asRecord(rawSnapshot.contract);
  const declaredYstChildren = Math.max(
    asOptionalNonNegativeInteger(contract.childrenCount) ?? 0,
    asOptionalNonNegativeInteger(familyInfo.numberOfYstStudents) ?? 0,
  );
  const preschoolChildren = Math.min(
    declaredYstChildren,
    asOptionalNonNegativeInteger(familyInfo.numberOfPreschoolStudents) ?? 0,
  );
  const fallbackEligibleChildren = asOptionalNonNegativeInteger(reviewSummary.eligibleChildCount) ?? 0;
  const fallbackTotalChildren = asOptionalNonNegativeInteger(reviewSummary.totalChildCount)
    ?? asOptionalNonNegativeInteger(familyInfo.numberOfDependents)
    ?? 0;
  const eligibleChildren = declaredYstChildren > 0
    ? Math.max(0, declaredYstChildren - preschoolChildren)
    : fallbackEligibleChildren;
  const totalChildren = declaredYstChildren > 0
    ? declaredYstChildren
    : Math.max(eligibleChildren, fallbackTotalChildren);
  return { totalChildren, eligibleChildren };
};

const toEpochMs = (value: unknown): number | null => {
  const numeric = asNumber(value);
  if (numeric > 0) {
    return numeric > 1_000_000_000_000 ? numeric : numeric * 1000;
  }
  const raw = readText(value);
  if (!raw) return null;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatDate = (value: unknown): string => {
  const ms = toEpochMs(value);
  if (!ms) return "Not provided";
  return new Date(ms).toLocaleDateString("en-US");
};

const formatDateTime = (value: Date): string =>
  value.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

const formatCurrency = (value: unknown): string =>
  asNumber(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const escapeHtml = (value: unknown): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const readFullName = (person: Record<string, unknown>): string => {
  const fullName =
    `${readText(person.firstName)} ${readText(person.lastName)}`.trim();
  return fullName || "Not provided";
};

const resolveParentForRelationship = (
  parents: [Record<string, unknown>, Record<string, unknown>],
  relationshipKey: "father" | "mother",
  fallback: Record<string, unknown>,
): Record<string, unknown> =>
  parents.find((parent) => readParentFinancialAidRelationshipKey(parent.relationship) === relationshipKey)
    ?? (readParentFinancialAidRelationshipKey(fallback.relationship) ? {} : fallback);

const formatAddress = (value: unknown): string => {
  const record = asRecord(value);
  if (Object.keys(record).length === 0) {
    return readText(value, "Not provided");
  }
  const street = readText(
    record.street,
    readText(record.street1, readText(record.addressLine1, readText(record.address))),
  );
  const line2 = readText(record.street2, readText(record.addressLine2, readText(record.unit)));
  const city = readText(record.city);
  const state = readText(record.state);
  const zip = readText(record.zipCode, readText(record.zip));
  const cityStateZip = [
    city,
    [state, zip].filter(Boolean).join(" "),
  ].filter(Boolean).join(", ");
  return [street, line2, cityStateZip].filter(Boolean).join(", ") || "Not provided";
};

const joinReportText = (...values: unknown[]): string =>
  values.map((value) => readText(value)).filter(Boolean).join(" - ");

const hasRecordKey = (record: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

const describeServicesToYSTForReport = (source: Record<string, unknown>): string => {
  const description = readText(source.servicesDescription);
  if (!hasRecordKey(source, "servicesToYST")) {
    return description;
  }
  return readRawBoolean(source.servicesToYST) ? description || "Yes" : "No";
};

export const buildPdfFriendlyApplicationReportHtml = (
  review: FinancialAidStaffDetailContext["review"],
  generatedAt: Date,
): string => {
  const reviewRecord = asRecord(review as unknown);
  const application = asRecord(reviewRecord.application);
  const sections = asRecord(application.sections);
  const recommendation = asRecord(application.recommendation);
  const finalAward = asRecord(application.finalAward);
  const familyInfo = asRecord(sections.familyInformation);
  const parent1 = asRecord(sections.parent1);
  const parent2 = asRecord(sections.parent2);
  const income = asRecord(sections.income);
  const assets = asRecord(sections.assets);
  const realEstate = asRecord(sections.realEstate);
  const vehicles = asRecord(sections.vehicles);
  const expenses = asRecord(sections.expenses);
  const majorExpenditures = asRecord(sections.majorExpenditures);
  const specialCircumstances = asRecord(sections.specialCircumstances);
  const additionalInfo = asRecord(sections.additionalInfo);
  const rawSnapshot = asRecord(additionalInfo[PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY]);
  const rawHomeAssets = asRecord(rawSnapshot.assets);
  const rawExpenses = asRecord(rawSnapshot.expenses);
  const rawMajorExp = asRecord(rawSnapshot.majorExp);
  const reviewSummary = asRecord(sections.reviewSummary);

  const familyName = readText(
    familyInfo.familyName,
    readText(application.familyName, "Family"),
  );
  const reference = readText(application.code, readText(application.id, "N/A"));
  const status = readText(application.status, "submitted")
    .replace(/_/g, " ")
    .toUpperCase();
  const generatedLabel = formatDateTime(generatedAt);
  const submittedDate = formatDate(
    application.submittedAt ?? application.createdAt ?? application.updatedAt,
  );

  const totalIncome = asNumber(
    income.totalAnnualIncome || reviewSummary.totalAnnualIncome,
  );
  const { totalChildren, eligibleChildren } = resolvePrintableReportChildCounts(
    familyInfo,
    reviewSummary,
    rawSnapshot,
  );
  const incomePerEligibleChild = eligibleChildren > 0
    ? totalIncome / eligibleChildren
    : asNumber(reviewSummary.incomePerEligibleChild);

  const recommendedPercent = asNumber(
    recommendation.recommendedPercent ?? finalAward.scholarshipPercent,
  );
  const recommendedAmount = asNumber(
    recommendation.recommendedAmount ?? finalAward.scholarshipAmount,
  );
  const confidence = readText(recommendation.confidence, "N/A");

  const previousYearIncome = asNumber(income.previousYearIncome || totalIncome);
  const projectedIncome = totalIncome;

  const parentRecords: [Record<string, unknown>, Record<string, unknown>] = [parent1, parent2];
  const fatherParent = resolveParentForRelationship(parentRecords, "father", parent1);
  const motherParent = resolveParentForRelationship(
    parentRecords,
    "mother",
    fatherParent === parent2 ? parent1 : parent2,
  );
  const fatherSnapshot = asRecord(rawSnapshot.father);
  const motherSnapshot = asRecord(rawSnapshot.mother);
  const fatherName = readFullName(fatherParent);
  const motherName = readFullName(motherParent);
  const fatherOccupation = readParentSnapshotTextFallback(fatherParent, fatherSnapshot, "occupation", "Not provided");
  const motherOccupation = readParentSnapshotTextFallback(motherParent, motherSnapshot, "occupation", "Not provided");
  const fatherEmployer = readParentSnapshotTextFallback(fatherParent, fatherSnapshot, "employer", "Not provided");
  const motherEmployer = readParentSnapshotTextFallback(motherParent, motherSnapshot, "employer", "Not provided");
  const fatherPhone = readText(
    fatherParent.cellPhone,
    readText(fatherParent.workPhone, "Not provided"),
  );
  const motherPhone = readText(
    motherParent.cellPhone,
    readText(motherParent.workPhone, "Not provided"),
  );
  const homeAddress = formatAddress(familyInfo.homeAddress);

  const primaryProperty = asRecord(asArray(realEstate.properties)[0]);
  const primaryResidenceValue = asNumber(
    realEstate.totalRealEstateValue ||
      primaryProperty.currentValue ||
      primaryProperty.estimatedValue,
  );
  const primaryResidenceEquity = asNumber(
    realEstate.totalRealEstateEquity || primaryProperty.equity,
  );
  const mortgageYearsAnswer = readText(rawHomeAssets.mortgageYears, "Not provided");
  const liquidSavings = asNumber(
    assets.checkingSavingsTotal || assets.cashOnHand,
  );
  const liquidInvestments = asNumber(
    assets.investmentsTotal || assets.retirementTotal,
  );

  const primaryVehicle = asRecord(asArray(vehicles.vehicles)[0]);
  const primaryVehicleLabel =
    `${readText(primaryVehicle.make)} ${readText(primaryVehicle.model)} ${readText(
      primaryVehicle.year,
    )}`
      .trim()
      .replace(/\s+/g, " ");
  const primaryVehicleValue = asNumber(primaryVehicle.estimatedValue);
  const medicalCoverageAnswer =
    joinReportText(expenses.medicalCoverage, expenses.medicalCoverageDetails) ||
    readText(specialCircumstances.medicalExpensesDetails) ||
    joinReportText(rawExpenses.medicalCoverage, rawExpenses.medicalCoverageDetails) ||
    "Not provided";
  const servicesToYSTAnswer =
    describeServicesToYSTForReport(majorExpenditures) ||
    describeServicesToYSTForReport(rawMajorExp) ||
    "Not provided";

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Application Report - ${escapeHtml(familyName)} Family</title>
    <style>
      :root { color-scheme: light; }
      body {
        font-family: Inter, "Helvetica Neue", Arial, sans-serif;
        color: #1f2937;
        margin: 0;
        padding: 40px;
        line-height: 1.45;
        font-size: 11px;
        background: white;
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        border-bottom: 2px solid #1c3b3e;
        padding-bottom: 10px;
        margin-bottom: 18px;
      }
      .header h1 {
        font-size: 40px;
        line-height: 1;
        margin: 0;
        font-weight: 800;
        color: #1c3b3e;
        text-transform: uppercase;
      }
      .subtitle {
        margin-top: 4px;
        font-size: 26px;
        font-weight: 600;
        color: #374151;
      }
      .header-meta {
        text-align: right;
        font-size: 12px;
        color: #6b7280;
        font-weight: 600;
      }
      .section-title {
        margin-top: 20px;
        margin-bottom: 10px;
        background: #1c3b3e;
        color: #ffffff;
        padding: 7px 10px;
        font-size: 12px;
        font-weight: 700;
        letter-spacing: 0.02em;
        text-transform: uppercase;
      }
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 16px;
      }
      .rec-box {
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        padding: 12px;
        margin-top: 12px;
      }
      .rec-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 16px;
        margin-top: 8px;
      }
      .parent-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
      }
      .box {
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        padding: 10px;
      }
      .asset-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 14px;
      }
      .label {
        text-transform: uppercase;
        font-size: 9px;
        color: #9ca3af;
        font-weight: 700;
        margin-bottom: 2px;
      }
      .value {
        font-size: 12px;
        color: #111827;
        font-weight: 700;
      }
      .row {
        margin-top: 7px;
      }
      .footer {
        margin-top: 20px;
        border-top: 1px solid #e5e7eb;
        padding-top: 8px;
        text-align: center;
        color: #9ca3af;
        font-size: 9px;
      }
      @media print {
        body { padding: 24px; }
        .section-title { -webkit-print-color-adjust: exact; color-adjust: exact; }
      }
    </style>
  </head>
  <body>
    <div class="header">
      <div>
        <h1>FULL APPLICATION REPORT</h1>
        <div class="subtitle">${escapeHtml(familyName)} Family · Ref: ${escapeHtml(reference)}</div>
      </div>
      <div class="header-meta">
        <div>Status: ${escapeHtml(status)}</div>
        <div>Generated: ${escapeHtml(generatedLabel)}</div>
      </div>
    </div>

    <div class="section-title">EXECUTIVE SUMMARY</div>
    <div class="summary-grid">
      <div><div class="label">Household Income</div><div class="value">${escapeHtml(formatCurrency(totalIncome))}</div></div>
      <div><div class="label">Income / Eligible Child</div><div class="value">${escapeHtml(formatCurrency(incomePerEligibleChild))}</div></div>
      <div><div class="label">Children (Total/Eligible)</div><div class="value">${escapeHtml(`${totalChildren} / ${eligibleChildren}`)}</div></div>
      <div><div class="label">Submission Date</div><div class="value">${escapeHtml(submittedDate)}</div></div>
    </div>
    <div class="rec-box">
      <div class="label">System Recommendation</div>
      <div class="rec-grid">
        <div><div class="label">Recommended %</div><div class="value">${escapeHtml(`${recommendedPercent}%`)}</div></div>
        <div><div class="label">Recommended Amount</div><div class="value">${escapeHtml(formatCurrency(recommendedAmount))}</div></div>
        <div><div class="label">Confidence</div><div class="value">${escapeHtml(confidence)}</div></div>
      </div>
    </div>

    <div class="section-title">PARENT INFORMATION</div>
    <div class="parent-grid">
      <div class="box">
        <div class="label" style="color:#1c3b3e">Father</div>
        <div class="row"><div class="label">Name</div><div class="value">${escapeHtml(fatherName)}</div></div>
        <div class="row"><div class="label">Occupation</div><div class="value">${escapeHtml(fatherOccupation)}</div></div>
        <div class="row"><div class="label">Employer</div><div class="value">${escapeHtml(fatherEmployer)}</div></div>
        <div class="row"><div class="label">Cell Phone</div><div class="value">${escapeHtml(fatherPhone)}</div></div>
      </div>
      <div class="box">
        <div class="label" style="color:#1c3b3e">Mother</div>
        <div class="row"><div class="label">Name</div><div class="value">${escapeHtml(motherName)}</div></div>
        <div class="row"><div class="label">Occupation</div><div class="value">${escapeHtml(motherOccupation)}</div></div>
        <div class="row"><div class="label">Employer</div><div class="value">${escapeHtml(motherEmployer)}</div></div>
        <div class="row"><div class="label">Cell Phone</div><div class="value">${escapeHtml(motherPhone)}</div></div>
      </div>
    </div>
    <div class="box" style="margin-top:10px;">
      <div class="label">Primary Address</div>
      <div class="value">${escapeHtml(homeAddress)}</div>
    </div>

    <div class="section-title">INCOME DETAILS</div>
    <div class="summary-grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));">
      <div><div class="label">Previous Year Gross Income</div><div class="value">${escapeHtml(formatCurrency(previousYearIncome))}</div></div>
      <div><div class="label">Current Year Projected</div><div class="value">${escapeHtml(formatCurrency(projectedIncome))}</div></div>
    </div>

    <div class="section-title">ASSETS & EQUITY</div>
    <div class="asset-grid">
      <div class="box">
        <div class="label" style="color:#1c3b3e">Primary Residence</div>
        <div class="row"><div class="label">Market Value</div><div class="value">${escapeHtml(formatCurrency(primaryResidenceValue))}</div></div>
        <div class="row"><div class="label">Estimated Equity</div><div class="value">${escapeHtml(formatCurrency(primaryResidenceEquity))}</div></div>
        <div class="row"><div class="label">Mortgage Years</div><div class="value">${escapeHtml(mortgageYearsAnswer)}</div></div>
      </div>
      <div class="box">
        <div class="label" style="color:#1c3b3e">Liquid Assets</div>
        <div class="row"><div class="label">Checking + Savings</div><div class="value">${escapeHtml(formatCurrency(liquidSavings))}</div></div>
        <div class="row"><div class="label">Investments + Retirement</div><div class="value">${escapeHtml(formatCurrency(liquidInvestments))}</div></div>
      </div>
      <div class="box">
        <div class="label" style="color:#1c3b3e">Vehicles</div>
        <div class="row"><div class="label">Primary Vehicle</div><div class="value">${escapeHtml(primaryVehicleLabel || "Not provided")}</div></div>
        <div class="row"><div class="label">Estimated Value</div><div class="value">${escapeHtml(formatCurrency(primaryVehicleValue))}</div></div>
      </div>
    </div>

    <div class="section-title">AVIGAL REQUESTED FIELDS</div>
    <div class="summary-grid" style="grid-template-columns: repeat(2, minmax(0, 1fr));">
      <div><div class="label">Medical Coverage</div><div class="value">${escapeHtml(medicalCoverageAnswer)}</div></div>
      <div><div class="label">Services to YST</div><div class="value">${escapeHtml(servicesToYSTAnswer)}</div></div>
    </div>

    <div class="footer">Confidential · Yeshiva Shaarei Tzion · PDF-friendly report preview</div>
  </body>
</html>`;
};

const SectionCard = ({
  icon: Icon,
  title,
  action,
  children,
}: {
  icon: typeof FileText;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <section
    className="bg-white rounded-xl border shadow-sm"
    style={{ borderColor: "#E5E7EB" }}
  >
    <div
      className="flex items-center justify-between gap-4 p-6 border-b"
      style={{ borderColor: "#F3F4F6" }}
    >
      <div className="flex items-center gap-3">
        <Icon size={18} style={{ color: "#404F4D" }} />
        <h2 className="text-lg font-semibold text-[#111827]">{title}</h2>
      </div>
      {action}
    </div>
    <div className="p-6">{children}</div>
  </section>
);

const toLabel = (value: string) =>
  value.replace(/[_-]+/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());

const documentIdentity = (documentRecord: FinancialAidDocumentRecord): string => {
  const documentId = readText(documentRecord.documentId);
  if (documentId) return `id:${documentId}`;
  const s3Key = readText(documentRecord.s3Key);
  if (s3Key) return `s3:${s3Key}`;
  return `type:${readText(documentRecord.documentType, 'document')}:file:${readText(documentRecord.fileName)}`;
};

const mergeDocumentRecords = (
  baseDocuments: FinancialAidDocumentRecord[],
  localDocuments: FinancialAidDocumentRecord[]
): FinancialAidDocumentRecord[] => {
  const merged = new Map<string, FinancialAidDocumentRecord>();
  for (const documentRecord of baseDocuments) {
    merged.set(documentIdentity(documentRecord), documentRecord);
  }
  for (const documentRecord of localDocuments) {
    const key = documentIdentity(documentRecord);
    const existing = merged.get(key);
    merged.set(key, existing ? { ...documentRecord, ...existing } : documentRecord);
  }
  return Array.from(merged.values());
};

const resolveDocumentTitle = (documentRecord: FinancialAidDocumentRecord): string => {
  const fileName = readText(documentRecord.fileName);
  if (Boolean(documentRecord.isUploaded) && fileName) {
    return fileName;
  }
  return toLabel(readText(documentRecord.documentType, fileName || 'Document'));
};

const resolveDocumentSubtitle = (documentRecord: FinancialAidDocumentRecord): string => {
  const fileName = readText(documentRecord.fileName);
  const documentTypeLabel = toLabel(readText(documentRecord.documentType, 'Supporting Document'));
  if (Boolean(documentRecord.isUploaded) && fileName) {
    return documentTypeLabel;
  }
  return fileName || 'No file uploaded';
};

const findConfirmedDocumentRecord = (
  response: unknown,
  fallback: FinancialAidDocumentRecord
): FinancialAidDocumentRecord => {
  const fallbackDocumentId = readText(fallback.documentId);
  const fallbackS3Key = readText(fallback.s3Key);
  const checklist = asArray<FinancialAidDocumentRecord>(asRecord(response).checklist);
  const confirmedRecord = checklist.find((item) => {
    const documentId = readText(item.documentId);
    const s3Key = readText(item.s3Key);
    return (fallbackDocumentId && documentId === fallbackDocumentId)
      || (fallbackS3Key && s3Key === fallbackS3Key);
  });
  return {
    ...fallback,
    ...(confirmedRecord ?? {}),
    isUploaded: true,
  };
};

const resolveDocumentUrl = (documentRecord: Record<string, unknown>): string => {
  const candidates = [
    documentRecord.downloadUrl,
    documentRecord.viewUrl,
    documentRecord.publicUrl,
    documentRecord.fileUrl,
    documentRecord.url,
    documentRecord.s3Url,
  ];
  for (const candidate of candidates) {
    const url = readText(candidate);
    if (url) {
      return url;
    }
  }
  return "";
};

const toUploadFailureMessage = (status: number): string => (
  status === 403
    ? 'Secure storage rejected this upload. Please choose the file again; if it still fails, contact support with this application open.'
    : `Document upload failed with status ${status}.`
);

const reportVariantLabel = (variant: ReportVariant): string => {
  if (variant === "summary") return "Financial Aid Summary";
  if (variant === "yst") return "YST Financial Aid Report";
  return "Full Application Report";
};

const isAbsoluteHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return (parsed.protocol === "http:" || parsed.protocol === "https:")
      && parsed.hostname.trim().length > 0;
  } catch {
    return false;
  }
};

const normalizeReportGeneratedAtSeconds = (value: unknown): number => {
  const numeric = asNumber(value);
  if (numeric <= 0) {
    return 0;
  }
  return Math.floor(numeric > 1_000_000_000_000 ? numeric / 1000 : numeric);
};

const isReportGeneratedAtFreshForStaleAfter = (
  generatedAt: unknown,
  staleAfter: number | null,
): boolean => staleAfter == null || normalizeReportGeneratedAtSeconds(generatedAt) >= staleAfter;

const isReportGeneratedAtStaleForStaleAfter = (
  generatedAt: unknown,
  staleAfter: number | null,
): boolean => {
  if (staleAfter == null) {
    return false;
  }
  const generatedAtSeconds = normalizeReportGeneratedAtSeconds(generatedAt);
  return generatedAtSeconds <= 0 || generatedAtSeconds < staleAfter;
};

const escapeScriptJson = (value: unknown): string =>
  JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

const buildReportPopupStaleGuardScript = (guard?: ReportPopupStaleGuard): string => {
  const applicationId = readText(guard?.applicationId);
  const generatedAt = normalizeReportGeneratedAtSeconds(guard?.generatedAt);
  if (!applicationId) {
    return "";
  }
  const payload = {
    applicationId,
    artifactId: readText(guard?.artifactId),
    channelName: FINANCIAL_AID_REPORT_STALE_CHANNEL,
    generatedAt,
    storageKey: financialAidReportStaleStorageKey(applicationId),
  };
  return `<script>
      (function () {
        var guard = ${escapeScriptJson(payload)};
        var iframe = document.querySelector('iframe');
        var warning = document.getElementById('financial-aid-report-preview-stale-warning');
        var buttons = Array.prototype.slice.call(document.querySelectorAll('a.button'));
        var alreadyMarkedStale = false;

        function parsePayload(value) {
          if (!value) return null;
          if (typeof value === 'object') return value;
          try {
            return JSON.parse(String(value));
          } catch (error) {
            return null;
          }
        }

        function isStaleSignal(value) {
          var signal = parsePayload(value);
          if (!signal || signal.applicationId !== guard.applicationId) {
            return false;
          }
          var staleAfter = Number(signal.staleAfter || 0);
          return Number.isFinite(staleAfter) && staleAfter > Number(guard.generatedAt || 0);
        }

        function disableReportButton(button) {
          button.removeAttribute('href');
          button.removeAttribute('download');
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('tabindex', '-1');
          button.addEventListener('click', function (event) {
            event.preventDefault();
          });
        }

        function markReportStale() {
          if (alreadyMarkedStale) return;
          alreadyMarkedStale = true;
          document.body.setAttribute('data-report-stale', 'true');
          document.title = 'Financial aid report stale';
          if (warning) {
            warning.hidden = false;
          }
          buttons.forEach(disableReportButton);
          if (iframe) {
            iframe.removeAttribute('src');
          }
        }

        try {
          if (isStaleSignal(window.localStorage && window.localStorage.getItem(guard.storageKey))) {
            markReportStale();
          }
        } catch (error) {
          // ignore blocked storage access
        }

        window.addEventListener('storage', function (event) {
          if (event.key === guard.storageKey && isStaleSignal(event.newValue)) {
            markReportStale();
          }
        });

        window.addEventListener(guard.channelName, function (event) {
          if (isStaleSignal(event.detail)) {
            markReportStale();
          }
        });

        if (typeof BroadcastChannel === 'function') {
          var channel = new BroadcastChannel(guard.channelName);
          channel.onmessage = function (event) {
            if (isStaleSignal(event.data)) {
              markReportStale();
            }
          };
          window.addEventListener('pagehide', function () {
            channel.close();
          });
        }
      })();
    </script>`;
};

const buildReportPopupNavigationFallbackHtml = (
  downloadUrl: string,
  fileName = "financial-aid-report.pdf",
  staleGuard?: ReportPopupStaleGuard,
): string => {
  const escapedUrl = escapeHtml(downloadUrl);
  const escapedFileName = escapeHtml(readText(fileName, "financial-aid-report.pdf"));
  const staleGuardScript = buildReportPopupStaleGuardScript(staleGuard);
  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Financial aid report ready</title>
    <style>
      :root { color-scheme: light; }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        min-height: 100vh;
        padding: 24px;
        font-family: system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
        color: #111827;
        background: linear-gradient(180deg, #f6fbf9 0%, #ffffff 42%);
      }
      .shell {
        width: min(1180px, 100%);
        margin: 0 auto;
      }
      .hero {
        margin-bottom: 18px;
        padding: 22px;
        border: 1px solid #dbe7e3;
        border-radius: 18px;
        background: #ffffff;
        box-shadow: 0 14px 36px rgba(31, 77, 77, 0.12);
      }
      .eyebrow {
        margin: 0 0 8px;
        color: #0f8f8f;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      h1 {
        margin: 0;
        font-size: clamp(24px, 4vw, 34px);
        line-height: 1.1;
      }
      .hint {
        margin: 10px 0 0;
        max-width: 760px;
        color: #4b5563;
        line-height: 1.55;
      }
      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 18px;
      }
      a.button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 42px;
        padding: 0 16px;
        border-radius: 12px;
        font-size: 14px;
        font-weight: 800;
        text-decoration: none;
      }
      .primary {
        color: #ffffff;
        background: #1f4d4d;
      }
      .secondary {
        color: #1f4d4d;
        border: 1px solid #bfd8d5;
        background: #f7fbfa;
      }
      iframe {
        display: block;
        width: 100%;
        height: 78vh;
        min-height: 520px;
        border: 1px solid #d1d5db;
        border-radius: 18px;
        background: #ffffff;
      }
      .fallback-note {
        margin: 10px 0 0;
        color: #6b7280;
        font-size: 13px;
      }
      .stale-warning {
        display: none;
        margin-top: 16px;
        padding: 12px 14px;
        border: 1px solid #f59e0b;
        border-radius: 12px;
        color: #78350f;
        background: #fffbeb;
        line-height: 1.45;
      }
      .stale-warning strong,
      .stale-warning span {
        display: block;
      }
      body[data-report-stale="true"] .stale-warning {
        display: block;
      }
      body[data-report-stale="true"] iframe {
        display: none;
      }
      body[data-report-stale="true"] a.button {
        cursor: not-allowed;
        opacity: 0.55;
        pointer-events: none;
      }
    </style>
  </head>
  <body>
    <main class="shell">
      <section class="hero">
        <p class="eyebrow">Financial Aid Report</p>
        <h1>Your PDF is ready</h1>
        <p class="hint">The report preview is embedded below. If the preview stays blank or black in this browser, use Preview PDF or Download PDF.</p>
        <div class="actions">
          <a class="button primary" href="${escapedUrl}" target="_blank" rel="noopener noreferrer">Preview PDF</a>
          <a class="button secondary" href="${escapedUrl}" download="${escapedFileName}" rel="noopener noreferrer">Download PDF</a>
        </div>
        <div id="financial-aid-report-preview-stale-warning" class="stale-warning" role="status" aria-live="polite" hidden>
          <strong>This report is stale after a staff correction.</strong>
          <span>Regenerate the report from Documents before opening or downloading the PDF.</span>
        </div>
      </section>
      <iframe title="Financial aid report PDF preview" src="${escapedUrl}"></iframe>
      <p class="fallback-note">This secure link expires. Regenerate the report from Intellio if either button stops working.</p>
    </main>
    ${staleGuardScript}
  </body>
</html>`;
};

const normalizeReportPreviewExpirySeconds = (value: unknown): number => {
  const numeric = asNumber(value);
  if (numeric <= 0) {
    return 0;
  }
  return Math.floor(numeric > 1_000_000_000_000 ? numeric / 1000 : numeric);
};

const buildReportPreviewUrlState = (
  artifactId: string | undefined,
  downloadUrl: string,
  expiresAt: unknown,
): ReportPreviewUrlState => {
  if (!isAbsoluteHttpUrl(downloadUrl)) {
    throw new Error(`Report PDF URL was not returned as an absolute http(s) link. Received report link: ${downloadUrl || "(empty)"}`);
  }
  const normalizedExpiresAt = normalizeReportPreviewExpirySeconds(expiresAt);
  if (!normalizedExpiresAt) {
    throw new Error(`Report PDF link was returned without a usable expiration. Received expiresAt: ${readText(expiresAt, "(empty)")}`);
  }
  if (normalizedExpiresAt - Math.floor(Date.now() / 1000) <= REPORT_PREVIEW_EXPIRY_SAFETY_SECONDS) {
    throw new Error(`Report PDF link was returned too close to expiration. Received expiresAt: ${normalizedExpiresAt}`);
  }
  return {
    artifactId: readText(artifactId),
    url: downloadUrl,
    expiresAt: normalizedExpiresAt,
  };
};

const detachReportPopupOpener = (popup: Window): void => {
  try {
    popup.opener = null;
  } catch {
    // ignore browser-managed popup lifecycle errors
  }
};

const writeReportPopupNavigationFallback = (
  popup: Window,
  downloadUrl: string,
  fileName?: string,
  staleGuard?: ReportPopupStaleGuard,
): boolean => {
  try {
    popup.document.open();
    popup.document.write(buildReportPopupNavigationFallbackHtml(downloadUrl, fileName, staleGuard));
    popup.document.close();
    return true;
  } catch {
    return false;
  }
};

const navigateReportPopupToSignedUrl = (
  popup: Window,
  downloadUrl: string,
  fileName?: string,
  staleGuard?: ReportPopupStaleGuard,
): boolean => {
  if (popup.closed) {
    return false;
  }

  const shellWritten = writeReportPopupNavigationFallback(popup, downloadUrl, fileName, staleGuard);
  if (shellWritten) {
    detachReportPopupOpener(popup);
  }

  return shellWritten;
};

const navigateReportPopupToDirectUrl = (
  popup: ReportPopupWindow,
  downloadUrl: string,
): boolean => {
  if (!popup || popup.closed) {
    return false;
  }
  try {
    popup.location.href = downloadUrl;
    detachReportPopupOpener(popup);
    return true;
  } catch {
    return false;
  }
};

const openReportPopupShellInNewTab = (
  downloadUrl: string,
  fileName?: string,
  staleGuard?: ReportPopupStaleGuard,
): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  const popup = window.open("about:blank", "_blank");
  if (!popup) {
    return false;
  }
  const shellWritten = writeReportPopupNavigationFallback(popup, downloadUrl, fileName, staleGuard);
  if (!shellWritten) {
    closePendingReportWindow(popup);
    return false;
  }
  detachReportPopupOpener(popup);
  return true;
};

const closePendingReportWindow = (popup: ReportPopupWindow): void => {
  try {
    if (popup && !popup.closed) {
      popup.close();
    }
  } catch {
    // ignore browser-managed popup lifecycle errors
  }
};

const openPendingReportWindow = (): ReportPopupWindow => {
  if (typeof window === "undefined" || typeof window.open !== "function") {
    return null;
  }
  try {
    const popup = window.open("about:blank", "_blank");
    if (!popup || popup.closed) {
      return null;
    }
    popup.document.open();
    popup.document.write([
      "<!doctype html>",
      "<html>",
      "<head><title>Preparing PDF</title></head>",
      '<body style="margin:0;background:#fff;color:#243b3a;font-family:system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;">',
      '<main style="padding:32px;line-height:1.5;">',
      '<h1 style="font-size:24px;margin:0 0 12px;">Preparing your PDF...</h1>',
      '<p style="margin:0;max-width:720px;">The Financial Aid report will open here when it is ready.</p>',
      "</main>",
      "</body>",
      "</html>",
    ].join(""));
    popup.document.close();
    detachReportPopupOpener(popup);
    return popup;
  } catch {
    return null;
  }
};

const coerceReportArtifact = (value: unknown): ReportArtifactRecord | null => {
  const record = asRecord(value);
  const variant = readText(record.variant) as ReportVariant;
  const artifactId = readText(record.artifactId);
  if (!artifactId || (variant !== "summary" && variant !== "full" && variant !== "yst")) {
    return null;
  }
  return {
    ...record,
    artifactId,
    variant,
    fileName: readText(record.fileName),
    appUrl: readText(record.appUrl),
    downloadUrl: readText(record.downloadUrl),
    appendix: asRecord(record.appendix) as ReportArtifactRecord["appendix"],
  };
};

const latestReportArtifactByVariant = (
  artifacts: ReportArtifactRecord[],
  variant: ReportVariant,
): ReportArtifactRecord | null => (
  artifacts
    .filter((artifact) => artifact.variant === variant)
    .sort((left, right) => asNumber(right.generatedAt) - asNumber(left.generatedAt))[0] ?? null
);

const isReportPreviewUrlFresh = (
  preview: ReportPreviewUrlState | undefined,
  artifact: ReportArtifactRecord,
): boolean => {
  if (!preview?.url || !preview.artifactId || preview.artifactId !== artifact.artifactId) {
    return false;
  }
  if (!isAbsoluteHttpUrl(preview.url)) {
    return false;
  }
  if (!preview.expiresAt) {
    return false;
  }
  return preview.expiresAt - Math.floor(Date.now() / 1000) > REPORT_PREVIEW_EXPIRY_SAFETY_SECONDS;
};

export function DocumentsTab() {
  const ctx = useOutletContext<FinancialAidStaffDetailContext>();
  const review = ctx.review;
  const uploadDocument = useUploadFinancialAidDocument();
  const confirmDocumentUpload = useConfirmFinancialAidDocumentUpload();
  const generateApplicationReport = useGenerateFinancialAidApplicationReport();
  const getReportDownloadUrl = useGetFinancialAidApplicationReportDownloadUrl();
  const reportArtifactsQuery = useListFinancialAidApplicationReportArtifacts(ctx.applicationId);
  const reportStaleAfter = typeof ctx.reportStaleAfter === "number" && Number.isFinite(ctx.reportStaleAfter)
    ? ctx.reportStaleAfter
    : null;
  const reportVersionKey = `${ctx.applicationId}:${reportStaleAfter ?? "none"}`;
  const addDocumentInputRef = useRef<HTMLInputElement | null>(null);
  const pendingUploadDocumentTypeRef = useRef("other_income_proof");
  const queryArtifactExpandedRef = useRef<string | null>(null);
  const queryArtifactRefetchingRef = useRef<string | null>(null);
  const queryArtifactRefetchAttemptedRef = useRef<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportTechnicalDetails, setReportTechnicalDetails] = useState<string | null>(null);
  const [generatedReportArtifacts, setGeneratedReportArtifacts] = useState<Partial<Record<ReportVariant, ReportArtifactRecord>>>({});
  const [refetchedReportArtifacts, setRefetchedReportArtifacts] = useState<ReportArtifactRecord[]>([]);
  const [expandedReportVariant, setExpandedReportVariant] = useState<ReportVariant | null>(null);
  const [previewUrls, setPreviewUrls] = useState<Partial<Record<ReportVariant, ReportPreviewUrlState>>>({});
  const [addDocumentError, setAddDocumentError] = useState<string | null>(null);
  const [addDocumentSuccess, setAddDocumentSuccess] = useState<string | null>(null);
  const [locallyConfirmedDocuments, setLocallyConfirmedDocuments] = useState<FinancialAidDocumentRecord[]>([]);
  const reviewDocuments = (Array.isArray(review?.documents) ? review?.documents : []).filter(isDefined);
  const documents = useMemo(
    () => mergeDocumentRecords(
      reviewDocuments.map((documentRecord) => asRecord(documentRecord)),
      locallyConfirmedDocuments
    ),
    [locallyConfirmedDocuments, reviewDocuments]
  );
  const listedReportArtifacts = useMemo(() => (
    asArray<unknown>(asRecord(reportArtifactsQuery.data).artifacts)
      .map(coerceReportArtifact)
      .filter(isDefined)
  ), [reportArtifactsQuery.data]);
  const reportArtifacts = useMemo(() => {
    const isFreshForCurrentCorrectionVersion = (artifact: ReportArtifactRecord): boolean =>
      isReportGeneratedAtFreshForStaleAfter(artifact.generatedAt, reportStaleAfter);
    return [
      ...listedReportArtifacts.filter(isFreshForCurrentCorrectionVersion),
      ...refetchedReportArtifacts.filter(isFreshForCurrentCorrectionVersion),
      ...Object.values(generatedReportArtifacts).filter(isDefined),
    ].filter(isFreshForCurrentCorrectionVersion);
  }, [generatedReportArtifacts, listedReportArtifacts, refetchedReportArtifacts, reportStaleAfter]);
  const summaryReportArtifact = latestReportArtifactByVariant(reportArtifacts, "summary");
  const fullReportArtifact = latestReportArtifactByVariant(reportArtifacts, "full");
  const ystReportArtifact = latestReportArtifactByVariant(reportArtifacts, "yst");
  const staleReportVariantsMissingFresh = useMemo(() => {
    if (reportStaleAfter == null) {
      return [];
    }
    const staleVariants = new Set(
      listedReportArtifacts
        .filter((artifact) => isReportGeneratedAtStaleForStaleAfter(artifact.generatedAt, reportStaleAfter))
        .map((artifact) => artifact.variant)
        .filter(isDefined),
    );
    if (staleVariants.size === 0) {
      return [];
    }
    const freshVariants = new Set(
      reportArtifacts
        .map((artifact) => artifact.variant)
        .filter(isDefined),
    );
    return Array.from(staleVariants).filter((variant) => !freshVariants.has(variant));
  }, [listedReportArtifacts, reportArtifacts, reportStaleAfter]);
  const showReportStaleBanner = reportStaleAfter != null
    && staleReportVariantsMissingFresh.length > 0;
  const expandedReportArtifact = expandedReportVariant === "summary"
    ? summaryReportArtifact
    : expandedReportVariant === "full"
      ? fullReportArtifact
      : expandedReportVariant === "yst"
        ? ystReportArtifact
        : null;

  const setReportErrorPresentation = (presentation: ReportErrorPresentation | null) => {
    setReportError(presentation?.message ?? null);
    setReportTechnicalDetails(presentation?.technicalDetails ?? null);
  };

  const readEffectiveReportStaleAfter = (): number | null => {
    const signalStaleAfter = readFinancialAidReportStaleSignal(ctx.applicationId)?.staleAfter;
    if (reportStaleAfter == null) {
      return signalStaleAfter ?? null;
    }
    return signalStaleAfter == null ? reportStaleAfter : Math.max(reportStaleAfter, signalStaleAfter);
  };

  const isGeneratedBeforeEffectiveReportStaleAfter = (generatedAt: unknown): boolean => {
    const effectiveReportStaleAfter = readEffectiveReportStaleAfter();
    return isReportGeneratedAtStaleForStaleAfter(generatedAt, effectiveReportStaleAfter);
  };

  const showStaleReportRegenerationMessage = (popupWindow?: ReportPopupWindow): void => {
    closePendingReportWindow(popupWindow ?? null);
    setReportErrorPresentation({ message: REPORT_STALE_AFTER_CORRECTION_ERROR });
  };

  useEffect(() => {
    setLocallyConfirmedDocuments([]);
    setGeneratedReportArtifacts({});
    setRefetchedReportArtifacts([]);
    setPreviewUrls({});
    setExpandedReportVariant(null);
    setReportErrorPresentation(null);
    queryArtifactExpandedRef.current = null;
    queryArtifactRefetchAttemptedRef.current = null;
  }, [ctx.applicationId]);

  useEffect(() => {
    setGeneratedReportArtifacts({});
    setRefetchedReportArtifacts([]);
    setPreviewUrls({});
    setExpandedReportVariant(null);
    setReportErrorPresentation(null);
    queryArtifactExpandedRef.current = null;
    queryArtifactRefetchAttemptedRef.current = null;
    if (ctx.applicationId) {
      void reportArtifactsQuery.refetch();
    }
  }, [reportVersionKey]);

  const storePreviewUrl = (
    variant: ReportVariant,
    artifactId: string | undefined,
    downloadUrl: string,
    expiresAt: unknown,
  ) => {
    const previewState = buildReportPreviewUrlState(artifactId, downloadUrl, expiresAt);
    setPreviewUrls((current) => ({
      ...current,
      [variant]: previewState,
    }));
  };

  const openReportDownloadUrl = (
    variant: ReportVariant,
    downloadUrl: string,
    popupWindow?: ReportPopupWindow,
    previewState?: ReportOpenPreviewState,
  ) => {
    if (isGeneratedBeforeEffectiveReportStaleAfter(previewState?.generatedAt)) {
      showStaleReportRegenerationMessage(popupWindow);
      return;
    }
    if (!isAbsoluteHttpUrl(downloadUrl)) {
      closePendingReportWindow(popupWindow ?? null);
      setExpandedReportVariant(variant);
      setReportErrorPresentation({
        message: REPORT_PDF_LINK_ERROR,
        technicalDetails: `Received report link: ${downloadUrl || "(empty)"}`,
      });
      return;
    }
    storePreviewUrl(variant, previewState?.artifactId, downloadUrl, previewState?.expiresAt);
    setExpandedReportVariant(variant);
    if (typeof window !== "undefined") {
      const opened = popupWindow ?? null;
      const popupWasAvailable = Boolean(opened && !opened.closed);
      const staleGuard = {
        applicationId: ctx.applicationId,
        artifactId: previewState?.artifactId,
        generatedAt: previewState?.generatedAt,
      };
      let popupNavigationFailed = false;
      if (opened && !opened.closed) {
        const navigated = navigateReportPopupToSignedUrl(opened, downloadUrl, previewState?.fileName, staleGuard);
        if (!navigated) {
          closePendingReportWindow(opened);
          popupNavigationFailed = true;
        }
      } else if (previewState?.openInNewTab) {
        popupNavigationFailed = !openReportPopupShellInNewTab(downloadUrl, previewState?.fileName, staleGuard);
      }
      if (popupNavigationFailed) {
        setExpandedReportVariant(variant);
        setReportErrorPresentation({
          message: popupWasAvailable
            ? REPORT_PDF_LINK_ERROR
            : "Your browser blocked the new tab. The report preview is open below.",
          technicalDetails: popupWasAvailable
            ? "Popup shell handoff failed for the signed report link."
            : undefined,
        });
      }
    } else {
      setExpandedReportVariant(variant);
      setReportErrorPresentation({
        message: "We couldn't open a new tab here. The report preview is open below.",
      });
    }
  };

  const handleGenerateReport = async (
    variant: ReportVariant,
    popupWindow?: ReportPopupWindow,
  ) => {
    setReportErrorPresentation(null);
    if (!ctx.applicationId) {
      closePendingReportWindow(popupWindow ?? null);
      setReportErrorPresentation({ message: REPORT_APP_CONTEXT_ERROR });
      return;
    }
    try {
      const response = await generateApplicationReport.mutateAsync({
        applicationId: ctx.applicationId,
        variant,
      });
      const responseRecord = asRecord(response);
      const artifact = coerceReportArtifact(response);
      let downloadUrl = readText(responseRecord.downloadUrl);
      let expiresAt = responseRecord.expiresAt;
      let fileName = artifact?.fileName || readText(responseRecord.fileName);
      const artifactId = artifact?.artifactId ?? readText(responseRecord.artifactId);
      const generatedAt = artifact?.generatedAt ?? responseRecord.generatedAt;
      if (artifact) {
        setGeneratedReportArtifacts((current) => ({
          ...current,
          [variant]: artifact,
        }));
      }
      if (!downloadUrl && artifactId) {
        const signedUrlResponse = await getReportDownloadUrl.mutateAsync({
          applicationId: ctx.applicationId,
          artifactId,
          disposition: "inline",
        });
        const signedUrlRecord = asRecord(signedUrlResponse);
        downloadUrl = readText(signedUrlRecord.downloadUrl);
        expiresAt = signedUrlRecord.expiresAt;
        fileName = readText(signedUrlRecord.fileName, fileName);
      }
      if (!downloadUrl) {
        throw new Error(REPORT_GENERATED_WITHOUT_LINK_ERROR);
      }
      openReportDownloadUrl(variant, downloadUrl, popupWindow, {
        artifactId,
        expiresAt,
        fileName,
        generatedAt,
        openInNewTab: !popupWindow,
      });
      await Promise.all([
        reportArtifactsQuery.refetch(),
        ctx.refetch(),
      ]);
    } catch (error) {
      closePendingReportWindow(popupWindow ?? null);
      setReportErrorPresentation(toReportError(
        error,
        `We couldn't generate ${reportVariantLabel(variant)} right now.`,
      ));
    }
  };

  const handleDownloadReportArtifact = async (
    variant: ReportVariant,
    artifact: ReportArtifactRecord | null,
    popupWindow?: ReportPopupWindow,
  ) => {
    setReportErrorPresentation(null);
    if (!ctx.applicationId) {
      closePendingReportWindow(popupWindow ?? null);
      setReportErrorPresentation({ message: REPORT_APP_CONTEXT_ERROR });
      return;
    }
    if (!artifact?.artifactId) {
      closePendingReportWindow(popupWindow ?? null);
      setReportErrorPresentation({
        message: `Generate the ${reportVariantLabel(variant)} first, then download it.`,
      });
      return;
    }
    if (isGeneratedBeforeEffectiveReportStaleAfter(artifact.generatedAt)) {
      closePendingReportWindow(popupWindow ?? null);
      showStaleReportRegenerationMessage();
      return;
    }
    try {
      const response = await getReportDownloadUrl.mutateAsync({
        applicationId: ctx.applicationId,
        artifactId: artifact.artifactId,
        disposition: "attachment",
      });
      const responseRecord = asRecord(response);
      const downloadUrl = readText(responseRecord.downloadUrl);
      const signedUrlState = buildReportPreviewUrlState(
        artifact.artifactId,
        downloadUrl,
        responseRecord.expiresAt,
      );
      if (navigateReportPopupToDirectUrl(popupWindow ?? null, signedUrlState.url)) {
        return;
      }
      closePendingReportWindow(popupWindow ?? null);
      if (typeof window === "undefined" || typeof window.open !== "function") {
        throw new Error("Report download is unavailable in this environment.");
      }
      const opened = window.open(signedUrlState.url, "_blank", "noopener,noreferrer");
      if (!opened) {
        throw new Error("Your browser blocked the report download tab.");
      }
      detachReportPopupOpener(opened);
    } catch (error) {
      closePendingReportWindow(popupWindow ?? null);
      setReportErrorPresentation(toReportError(
        error,
        `We couldn't download ${reportVariantLabel(variant)} right now.`,
      ));
    }
  };

  const handleToggleReportPreview = async (
    variant: ReportVariant,
    artifact: ReportArtifactRecord | null,
  ) => {
    setReportErrorPresentation(null);
    if (expandedReportVariant === variant) {
      setExpandedReportVariant(null);
      return;
    }
    if (!ctx.applicationId) {
      setReportErrorPresentation({ message: REPORT_APP_CONTEXT_ERROR });
      return;
    }
    if (!artifact?.artifactId) {
      setReportErrorPresentation({
        message: `Generate the ${reportVariantLabel(variant)} first, then open the preview.`,
      });
      return;
    }
    if (isGeneratedBeforeEffectiveReportStaleAfter(artifact.generatedAt)) {
      showStaleReportRegenerationMessage();
      return;
    }
    const existingPreviewUrl = previewUrls[variant];
    if (isReportPreviewUrlFresh(existingPreviewUrl, artifact)) {
      setExpandedReportVariant(variant);
      return;
    }
    try {
      const response = await getReportDownloadUrl.mutateAsync({
        applicationId: ctx.applicationId,
        artifactId: artifact.artifactId,
        disposition: "inline",
      });
      const downloadUrl = readText(asRecord(response).downloadUrl);
      if (!downloadUrl) {
        throw new Error(REPORT_PDF_LINK_ERROR);
      }
      storePreviewUrl(variant, artifact.artifactId, downloadUrl, asRecord(response).expiresAt);
      setExpandedReportVariant(variant);
    } catch (error) {
      setReportErrorPresentation(toReportError(
        error,
        `We couldn't open ${reportVariantLabel(variant)} preview right now.`,
      ));
    }
  };

  const handleReportPreviewLoadError = (
    variant: ReportVariant,
    artifact: ReportArtifactRecord | null,
  ) => {
    const previewUrl = readText(previewUrls[variant]?.url);
    setPreviewUrls((current) => {
      const next = { ...current };
      delete next[variant];
      return next;
    });
    setReportErrorPresentation({
      message: REPORT_PREVIEW_LOAD_ERROR,
      technicalDetails: `Preview failed for ${readText(artifact?.artifactId, "unknown artifact")}: ${previewUrl || "(empty)"}`,
    });
  };

  useEffect(() => {
    const variant = expandedReportVariant;
    const artifact = expandedReportArtifact;
    if (reportError || !variant || !ctx.applicationId || !artifact?.artifactId) {
      return undefined;
    }
    const artifactId = artifact.artifactId;
    if (isReportPreviewUrlFresh(previewUrls[variant], artifact)) {
      return undefined;
    }

    let cancelled = false;
    void (async () => {
      try {
        const response = await getReportDownloadUrl.mutateAsync({
          applicationId: ctx.applicationId,
          artifactId,
          disposition: "inline",
        });
        if (cancelled) {
          return;
        }
        const downloadUrl = readText(asRecord(response).downloadUrl);
        if (!downloadUrl) {
          throw new Error(REPORT_PDF_LINK_ERROR);
        }
        storePreviewUrl(variant, artifactId, downloadUrl, asRecord(response).expiresAt);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setReportErrorPresentation(toReportError(
          error,
          `We couldn't refresh ${reportVariantLabel(variant)} preview right now.`,
        ));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    ctx.applicationId,
    expandedReportArtifact?.artifactId,
    expandedReportVariant,
    getReportDownloadUrl,
    previewUrls,
    reportError,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const artifactId = readText(new URLSearchParams(window.location.search).get("reportArtifact"));
    if (!artifactId || queryArtifactExpandedRef.current === artifactId) {
      return;
    }
    const artifact = reportArtifacts.find((candidate) => candidate.artifactId === artifactId);
    if (artifact?.variant) {
      queryArtifactExpandedRef.current = artifactId;
      void handleToggleReportPreview(artifact.variant, artifact);
      return;
    }
    if (queryArtifactRefetchingRef.current === artifactId) {
      return;
    }
    if (queryArtifactRefetchAttemptedRef.current === artifactId) {
      return;
    }

    queryArtifactRefetchAttemptedRef.current = artifactId;
    queryArtifactRefetchingRef.current = artifactId;
    void (async () => {
      try {
        const refetchResult = await reportArtifactsQuery.refetch();
        if (queryArtifactExpandedRef.current === artifactId) {
          return;
        }
        const refreshedArtifacts = asArray<unknown>(asRecord(asRecord(refetchResult).data).artifacts)
          .map(coerceReportArtifact)
          .filter(isDefined);
        setRefetchedReportArtifacts(refreshedArtifacts);
        const matchedArtifact = refreshedArtifacts.find((candidate) => candidate.artifactId === artifactId);
        if (matchedArtifact?.variant) {
          queryArtifactExpandedRef.current = artifactId;
          void handleToggleReportPreview(matchedArtifact.variant, matchedArtifact);
        } else {
          setReportErrorPresentation({
            message: REPORT_ARTIFACT_NOT_FOUND_ERROR,
            technicalDetails: `Report artifact deep link was not found after refresh: ${artifactId}`,
          });
        }
      } finally {
        if (queryArtifactRefetchingRef.current === artifactId) {
          queryArtifactRefetchingRef.current = null;
        }
      }
    })();
  }, [reportArtifacts, reportArtifactsQuery]);

  const triggerUploadForDocumentType = (documentType: string) => {
    pendingUploadDocumentTypeRef.current = readText(
      documentType,
      "other_income_proof",
    );
    addDocumentInputRef.current?.click();
  };

  const handleMissingDocumentRowKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    documentType: string,
  ) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    triggerUploadForDocumentType(documentType);
  };

  const handleAddDocumentFile = async (file: File | null) => {
    if (!file || !ctx.applicationId) {
      return;
    }
    setAddDocumentError(null);
    setAddDocumentSuccess(null);
    try {
      const fileType = inferFinancialAidUploadFileType(file);
      const validationError = getFinancialAidUploadFileValidationError(file);
      if (validationError) {
        throw new Error(validationError);
      }

      const documentType =
        pendingUploadDocumentTypeRef.current || "other_income_proof";

      const uploadInfo = await uploadDocument.mutateAsync({
        applicationId: ctx.applicationId,
        documentType,
        fileName: file.name,
        fileType,
        fileSizeBytes: file.size,
      }) as FinancialAidUploadInfo;

      const uploadUrl = readText(uploadInfo?.uploadUrl);
      const documentId = readText(uploadInfo?.documentId);
      const s3Key = readText(uploadInfo?.s3Key);
      if (!uploadUrl || !documentId || !s3Key) {
        throw new Error(
          "Upload URL, S3 key, or document id was not returned by uploadFinancialAidDocument.",
        );
      }

      const putResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: buildBrowserUploadHeaders({
          uploadHeaders: uploadInfo.uploadHeaders,
          uploadUrl,
          fileType,
        }),
      });
      if (!putResponse.ok) {
        throw new Error(toUploadFailureMessage(putResponse.status));
      }

      const confirmResponse = await confirmDocumentUpload.mutateAsync({
        applicationId: ctx.applicationId,
        documentType,
        documentId,
        s3Key,
        fileName: file.name,
        fileType,
        fileSizeBytes: file.size,
      });
      const confirmedDocument = findConfirmedDocumentRecord(confirmResponse, {
        documentType,
        isRequired: true,
        isUploaded: true,
        uploadedAt: Math.floor(Date.now() / 1000),
        documentId,
        s3Key,
        fileName: file.name,
        fileType,
        fileSizeBytes: file.size,
        confirmedAt: Math.floor(Date.now() / 1000),
      });
      setLocallyConfirmedDocuments((current) => mergeDocumentRecords(current, [confirmedDocument]));
      await ctx.refetch();
      setAddDocumentSuccess(`Uploaded ${file.name}.`);
    } catch (error) {
      const message = toErrorMessage(error);
      setAddDocumentError(message || "Failed to upload document.");
    } finally {
      if (addDocumentInputRef.current) {
        addDocumentInputRef.current.value = "";
      }
    }
  };

  const handleOpenDocument = (documentRecord: Record<string, unknown>) => {
    setAddDocumentError(null);
    if (typeof window === "undefined") {
      setAddDocumentError(
        "Document preview is unavailable in this environment.",
      );
      return;
    }
    const url = resolveDocumentUrl(documentRecord);
    if (!url) {
      setAddDocumentError(
        "Document preview URL is not available for this file yet.",
      );
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="financial-aid-documents-tab" className="space-y-6 max-w-5xl">
      <SectionCard icon={FileText} title="Application Reports">
        {showReportStaleBanner ? (
          <div
            data-testid="financial-aid-report-stale-banner"
            className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900"
          >
            Reports need to be regenerated after staff edits to application data.
          </div>
        ) : null}
        {([
          ["summary", summaryReportArtifact],
          ["full", fullReportArtifact],
          ["yst", ystReportArtifact],
        ] as Array<[ReportVariant, ReportArtifactRecord | null]>).map(([variant, artifact]) => {
          const isExpanded = expandedReportVariant === variant;
          const previewState = previewUrls[variant];
          const previewUrl = artifact && isReportPreviewUrlFresh(previewState, artifact)
            ? readText(previewState?.url)
            : "";
          const hasPreviewDeepLink = Boolean(readText(artifact?.appUrl));
          return (
            <div
              key={variant}
              className="rounded-xl border p-4 mb-3 last:mb-0"
              style={{ borderColor: "#E5E7EB" }}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <FileText
                    size={18}
                    className="mt-0.5 text-[#6B7280] flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#111827]">
                      {reportVariantLabel(variant)}
                    </p>
                    <p
                      className="sr-only"
                      data-testid={`financial-aid-report-app-link-${variant}`}
                    >
                      {hasPreviewDeepLink
                        ? "Preview deep link generated."
                        : "No preview deep link generated yet."}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {artifact ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          void handleToggleReportPreview(variant, artifact);
                        }}
                        data-testid={`financial-aid-report-preview-toggle-${variant}`}
                        className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold text-[#1F4D4D] disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ borderColor: "#D1D5DB" }}
                        disabled={getReportDownloadUrl.isPending}
                      >
                        <FileText size={14} />
                        {isExpanded ? "Hide preview" : "Preview"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (isGeneratedBeforeEffectiveReportStaleAfter(artifact.generatedAt)) {
                            showStaleReportRegenerationMessage();
                            return;
                          }
                          const popupWindow = openPendingReportWindow();
                          void handleDownloadReportArtifact(variant, artifact, popupWindow);
                        }}
                        data-testid={`financial-aid-report-download-${variant}`}
                        className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold text-[#1F4D4D] disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{ borderColor: "#D1D5DB" }}
                        disabled={getReportDownloadUrl.isPending}
                      >
                        <Download size={14} />
                        Download
                      </button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      const popupWindow = openPendingReportWindow();
                      void handleGenerateReport(variant, popupWindow);
                    }}
                    data-testid={`financial-aid-report-generate-${variant}`}
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-[#1F4D4D] disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={generateApplicationReport.isPending}
                  >
                    {artifact ? <RefreshCw size={14} /> : <ExternalLink size={14} />}
                    {generateApplicationReport.isPending
                      ? "Generating..."
                      : artifact
                        ? "Regenerate"
                        : "Generate"}
                  </button>
                </div>
              </div>
              {isExpanded && previewUrl ? (
                <>
                  <iframe
                    title={`${reportVariantLabel(variant)} PDF preview`}
                    data-testid={`financial-aid-report-preview-${variant}`}
                    src={previewUrl}
                    className="mt-4 h-[560px] w-full rounded-lg border bg-white"
                    style={{ borderColor: "#E5E7EB" }}
                    onError={() => handleReportPreviewLoadError(variant, artifact)}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-[#6B7280]">
                    <span>If the preview stays blank, use Download or regenerate the report.</span>
                    <button
                      type="button"
                      data-testid={`financial-aid-report-preview-help-${variant}`}
                      className="text-[#1F4D4D] underline underline-offset-2"
                      onClick={() => handleReportPreviewLoadError(variant, artifact)}
                    >
                      Show support details
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          );
        })}
        {reportError ? (
          <SupportErrorMessage
            className="mt-3"
            technicalDetails={reportTechnicalDetails ?? undefined}
          >
            {reportError}
          </SupportErrorMessage>
        ) : null}
        {reportArtifactsQuery.error ? (
          <SupportErrorMessage className="mt-3">
            {toErrorMessage(reportArtifactsQuery.error) || "Failed to load report artifacts."}
          </SupportErrorMessage>
        ) : null}
      </SectionCard>

      <SectionCard
        icon={FileText}
        title="Supporting Documents"
        action={
          <>
            <input
              ref={addDocumentInputRef}
              type="file"
              accept="application/pdf,image/jpeg,image/png"
              className="hidden"
              data-testid="financial-aid-documents-add-document-input"
              onChange={(event) => {
                const file = event.currentTarget.files?.[0] ?? null;
                void handleAddDocumentFile(file);
              }}
            />
            <button
              type="button"
              onClick={() => triggerUploadForDocumentType("other_income_proof")}
              data-testid="financial-aid-documents-add-document-button"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white bg-[#28E2A7] disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={
                uploadDocument.isPending || confirmDocumentUpload.isPending
              }
              title="Upload a supporting document via uploadFinancialAidDocument mutation."
            >
              <Plus size={14} />
              {uploadDocument.isPending || confirmDocumentUpload.isPending
                ? "Uploading..."
                : "Add Document"}
            </button>
          </>
        }
      >
        {addDocumentError ? (
          <p
            className="mb-3 text-xs font-semibold text-[#B91C1C]"
            data-testid="financial-aid-documents-add-document-status"
          >
            {addDocumentError}
          </p>
        ) : null}
        {addDocumentSuccess ? (
          <p
            className="mb-3 text-xs font-semibold text-[#237062]"
            data-testid="financial-aid-documents-add-document-status"
          >
            {addDocumentSuccess}
          </p>
        ) : null}
        {!addDocumentError && !addDocumentSuccess ? (
          <p
            className="sr-only"
            data-testid="financial-aid-documents-add-document-status"
          >
            Ready
          </p>
        ) : null}
        {documents.length === 0 ? (
          <p className="text-sm text-[#6B7280]">
            Supporting documents are syncing.
          </p>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => {
              const documentType = readText(
                doc.documentType,
                "other_income_proof",
              );
              const uploaded =
                Boolean(doc.isUploaded) &&
                Boolean(
                  readText(doc.fileName) ||
                    readText(doc.s3Key) ||
                    resolveDocumentUrl(doc),
                );
              const displayName = resolveDocumentTitle(doc);
              const subtitle = resolveDocumentSubtitle(doc);
              const rowClassName = `rounded-xl border px-4 py-3 flex items-center justify-between gap-4 ${
                uploaded
                  ? ""
                  : "cursor-pointer hover:bg-[#F9FAFB] focus:outline-none focus:ring-2 focus:ring-[#28E2A7]/40"
              }`;
              return (
                <div
                  key={documentIdentity(doc)}
                  className={rowClassName}
                  style={{ borderColor: "#E5E7EB" }}
                  role={uploaded ? undefined : "button"}
                  tabIndex={uploaded ? undefined : 0}
                  data-testid={`financial-aid-document-row-${documentType}`}
                  onClick={
                    uploaded
                      ? undefined
                      : () => triggerUploadForDocumentType(documentType)
                  }
                  onKeyDown={
                    uploaded
                      ? undefined
                      : (event) =>
                          handleMissingDocumentRowKeyDown(event, documentType)
                  }
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <FileText
                      size={18}
                      className="mt-0.5 text-[#6B7280] flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#111827] truncate">{displayName}</p>
                      <p className="text-xs text-[#9CA3AF] truncate">{subtitle}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                        uploaded
                          ? "text-[#237062] bg-[#ECFDF5]"
                          : "text-[#FE2C3B] bg-[#FEE2E2]"
                      }`}
                    >
                      <CheckCircle2 size={12} />
                      {uploaded ? "Uploaded" : "Missing"}
                    </span>
                    {uploaded ? (
                      <button
                        type="button"
                        className="rounded-md px-3 py-1.5 text-sm font-semibold text-white bg-[#1F4D4D] hover:opacity-90 transition-opacity"
                        onClick={() => handleOpenDocument(doc)}
                      >
                        View
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold text-white bg-[#28E2A7] hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
                        disabled={
                          uploadDocument.isPending ||
                          confirmDocumentUpload.isPending
                        }
                        data-testid={`financial-aid-document-upload-button-${documentType}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          triggerUploadForDocumentType(documentType);
                        }}
                      >
                        Upload
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
